/**
 * Backtest Engine Documentation - QSL Compatible
 */
import type { DocPage } from '../types';

export const engineDocs: DocPage[] = [
  {
    id: 'engine/execution/order-timing',
    title: 'Order Timing',
    category: 'engine',
    subcategory: 'execution',
    tags: ['engine', 'execution', 'timing', 'fills', 'orders', 'qsl'],
    version: '2.0',
    related: ['api/orders/market', 'engine/fills/marketOrders'],
    order: 1,
    description: 'When and how orders are evaluated and filled',
    content: `# Order Timing

Understanding order timing is critical for writing correct strategies.

## The Bar Cycle

Each bar in a backtest follows this exact sequence:

\`\`\`
1. Bar i closes
2. onBar(ctx, i) executes — you see Bar i's OHLCV
3. Orders are queued (but NOT filled yet)
4. Bar i+1 opens
5. Pending orders are evaluated against Bar i+1
6. Fills occur at Bar i+1 prices (± slippage)
7. Repeat from step 1
\`\`\`

## Key Implications

### You cannot trade within a bar

When \`onBar\` runs, the bar is already closed. Any orders you place will fill on the **next** bar.

\`\`\`javascript
function onBar(ctx, i) {
  // Bar i: close = 100
  ctx.order.market('ASSET', 1, { signal: 'buy' });
  // Order fills at Bar i+1 open, NOT at 100
}
\`\`\`

### Position is not updated immediately

\`\`\`javascript
function onBar(ctx, i) {
  ctx.order.market('ASSET', 1, { signal: 'buy' });
  
  const pos = ctx.position('ASSET');
 // Still 0! Order hasn't filled yet.
}
\`\`\`

### Order evaluation order

When multiple orders are pending, they evaluate in this order:

1. **Stop orders** (stop-loss, trailing stops)
2. **Limit orders**
3. **Market orders**

Within each category, orders evaluate in placement order.

## Intrabar Price Assumptions

The engine does NOT simulate intrabar price movement. For a bar with:

\`\`\`
Open: 100, High: 105, Low: 98, Close: 102
\`\`\`

We assume the price path was: Open → High → Low → Close

This affects stop and limit order fills.

## Example Timeline

\`\`\`
Bar 1: O=100, H=102, L=99, C=101
  → onBar(ctx, 1) runs
  → You place ctx.order.market('ASSET', 1)
  
Bar 2: O=101.5, H=103, L=100, C=102
  → Market order fills at 101.5 (± slippage)
  → onBar(ctx, 2) runs
  → ctx.position('ASSET').qty is now 1
\`\`\`

## Why This Matters

This execution model prevents:

- **Look-ahead bias** — You can't act on data you wouldn't have in live trading
- **Unrealistic fills** — No filling at exact highs/lows
- **Slippage denial** — Market orders don't fill at the price you saw

## Related

- [Market Order Fills](/docs/engine/fills/marketOrders)
- [Slippage Model](/docs/engine/fills/slippageModel)
`,
  },
  {
    id: 'engine/fills/marketOrders',
    title: 'Market Order Fills',
    category: 'engine',
    subcategory: 'fills',
    tags: ['engine', 'fills', 'market', 'slippage', 'qsl'],
    version: '2.0',
    related: ['api/orders/market', 'engine/fills/slippageModel'],
    order: 1,
    description: 'How market orders fill in the backtest engine',
    content: `# Market Order Fills

Market orders are the simplest order type. They fill at the next bar's open.

## Fill Price Calculation

\`\`\`
Fill Price = Next Bar Open × (1 + slippage × direction)
\`\`\`

Where:
- \`direction\` = +1 for buys (slippage works against you)
- \`direction\` = -1 for sells

## Example

Configuration:
\`\`\`javascript
// Backtest settings
{
  slippage: 0.001  // 0.1%
}
\`\`\`

Bar i+1 opens at 100:
- Market buy fills at: 100 × 1.001 = 100.10
- Market sell fills at: 100 × 0.999 = 99.90

## Guaranteed Fill

Market orders **always fill** (assuming sufficient balance). They cannot:
- Partially fill
- Be rejected
- Expire

## Gap Handling

If the market gaps significantly, market orders still fill at the open:

\`\`\`
Bar i: Close = 100
Bar i+1: Open = 110 (10% gap up)
→ Market buy placed on Bar i fills at 110 (plus slippage)
\`\`\`

This models reality: market orders in gapping markets can have significant slippage.

## Code Example

\`\`\`javascript
function onBar(ctx, i) {
  if (q.crossOver(ctx.ind.fastEma, ctx.ind.slowEma, i)) {
    // Order placed here...
    ctx.order.market('ASSET', 1, { signal: 'buy' });
    // ...fills on bar i+1 at open ± slippage
  }
}
\`\`\`

## Fill Confirmation

You can track fills using state:

\`\`\`javascript
function init(ctx) {
  ctx.state.prevQty = 0;
}

function onBar(ctx, i) {
  const pos = ctx.position('ASSET');
  
  // Detect fill by comparing to previous bar
  if (pos.qty > 0 && ctx.state.prevQty === 0) {
    // Just entered a position
    ctx.state.entryBar = i;
    ctx.state.entryPrice = pos.avgPrice;
  }
  
  ctx.state.prevQty = pos.qty;
}
\`\`\`

## Related

- [Slippage Model](/docs/engine/fills/slippageModel)
- [Order Timing](/docs/engine/execution/order-timing)
`,
  },
  {
    id: 'engine/fills/slippageModel',
    title: 'Slippage Model',
    category: 'engine',
    subcategory: 'fills',
    tags: ['engine', 'fills', 'slippage', 'realistic', 'qsl'],
    version: '2.0',
    related: ['api/orders/market', 'engine/fills/marketOrders'],
    order: 2,
    description: 'How slippage is modeled in backtests',
    content: `# Slippage Model

Slippage models the difference between expected and actual fill prices.

## Default Model

The engine uses a **percentage-based** slippage model:

\`\`\`
Fill Price = Open × (1 + slippage × direction)
\`\`\`

| Direction | Calculation | Effect |
|-----------|-------------|--------|
| Buy (+1) | Open × (1 + slippage) | Pay more |
| Sell (-1) | Open × (1 - slippage) | Receive less |

## Configuration

Slippage is set in the backtest configuration:

\`\`\`javascript
// Backtest settings
{
  slippage: 0.001,  // 0.1% slippage
  // or
  slippage: 0.005,  // 0.5% for less liquid markets
}
\`\`\`

## Examples

With slippage = 0.001 (0.1%):

| Order | Open Price | Fill Price |
|-------|------------|------------|
| Buy 1 | 100.00 | 100.10 |
| Sell 1 | 100.00 | 99.90 |

## Why Slippage Matters

A strategy that looks profitable without slippage often fails with realistic slippage:

\`\`\`
No slippage:  +15% return
0.1% slippage: +8% return
0.5% slippage: -2% return (unprofitable!)
\`\`\`

## Best Practices

### Always test with slippage

Never evaluate a strategy at 0% slippage. Use realistic values:

| Market | Suggested Slippage |
|--------|-------------------|
| BTC/USDT | 0.05% - 0.1% |
| Major alts | 0.1% - 0.2% |
| Small alts | 0.3% - 0.5% |
| Low liquidity | 0.5% - 1.0% |

### Sensitivity testing

Test your strategy at multiple slippage levels:

\`\`\`
Run 1: slippage = 0.05%
Run 2: slippage = 0.1%
Run 3: slippage = 0.2%
\`\`\`

If profitability disappears at higher slippage, the strategy may not be robust.

## Related

- [Market Order Fills](/docs/engine/fills/marketOrders)
- [Order Timing](/docs/engine/execution/order-timing)
`,
  },
  {
    id: 'engine/determinism/reproducibility',
    title: 'Reproducibility',
    category: 'engine',
    subcategory: 'determinism',
    tags: ['engine', 'determinism', 'reproducibility', 'consistency', 'qsl'],
    version: '2.0',
    related: ['getting-started/overview'],
    order: 1,
    description: 'How Quanthop ensures reproducible results',
    content: `# Reproducibility

Every backtest in Quanthop is fully reproducible.

## The Guarantee

Given:
- Same strategy code
- Same parameters
- Same data range
- Same configuration

The result will be **identical**, including:
- Every trade entry/exit
- All metrics (PnL, Sharpe, drawdown, etc.)
- The exact sequence of fills

## How It Works

### No Hidden State

The engine maintains no state between runs. Each backtest starts fresh.

### Deterministic Execution

- Order evaluation follows fixed rules
- Fill prices are calculated, not simulated
- No random number generation

### Pre-computed Indicators

Indicators declared in \`init()\` are computed once before \`onBar()\` runs:

\`\`\`javascript
function init(ctx) {
  ctx.indicator('ema', 'EMA', { period: 20 });
}

// By the time onBar runs, ctx.ind.ema contains all values
// for all bars - computed deterministically
\`\`\`

## What Breaks Reproducibility

These patterns will cause different results between runs:

### Random numbers

\`\`\`javascript
// FORBIDDEN
function onBar(ctx, i) {
  if (Math.random() > 0.5) {
    ctx.order.market('ASSET', 1);
  }
}
\`\`\`

### External data

\`\`\`javascript
// FORBIDDEN
function onBar(ctx, i) {
  const signal = await fetchExternalAPI();
}
\`\`\`

### Wall clock time

\`\`\`javascript
// FORBIDDEN - uses current time
function onBar(ctx, i) {
  if (new Date().getHours() > 12) {
    ctx.order.market('ASSET', 1);
  }
}
\`\`\`

## Verification

You can verify reproducibility:

1. Run a backtest
2. Note the exact metrics
3. Run again with identical settings
4. Results must match exactly

If they don't, there's a bug in your strategy (or the platform — please report it).

## Why This Matters

Reproducibility enables:

- **Valid comparisons** — Different parameter sets compared fairly
- **Debugging** — Isolate issues by reproducing them
- **Auditing** — Prove results to yourself and others
- **Trust** — Know that "good" results aren't luck

## Related

- [Overview](/docs/getting-started/overview) — Platform philosophy
`,
  },
  {
    id: 'engine/indicators/computation',
    title: 'Indicator Computation',
    category: 'engine',
    subcategory: 'indicators',
    tags: ['engine', 'indicators', 'computation', 'performance', 'qsl'],
    version: '2.0',
    related: ['api/lifecycle/init', 'getting-started/strategy-lifecycle'],
    order: 1,
    description: 'How indicators are computed by the engine',
    content: `# Indicator Computation

Understanding how indicators are computed helps you write efficient strategies.

## Host-Side Computation

Unlike traditional backtesting where you compute indicators in your strategy code, QSL indicators are computed by the engine **before** your \`onBar()\` runs.

\`\`\`
1. define(ctx) runs - parameters collected
2. init(ctx) runs - indicator declarations collected
3. ENGINE COMPUTES ALL INDICATORS (host-side, fast)
4. onBar(ctx, i) runs - indicators already available
\`\`\`

## Why This Matters

### Performance

Computing a 200-period EMA on 10,000 bars requires 10,000 calculations. If done in JavaScript for every bar, that's 100 million operations.

With host-side computation:
- Computed once in optimized native code
- Results passed to your strategy as arrays
- 10x-100x faster than in-strategy computation

### Consistency

All strategies use the same indicator implementations:
- No bugs in custom indicator code
- Results match across strategies
- Platform-verified accuracy

## Declaration in init()

\`\`\`javascript
function init(ctx) {
  // These are declarations, not computations
  ctx.indicator('ema', 'EMA', { period: ctx.p.period, source: 'close' });
  ctx.indicator('rsi', 'RSI', { period: 14 });
  ctx.indicator('macd', 'MACD', { 
    fastPeriod: 12, 
    slowPeriod: 26, 
    signalPeriod: 9 
  });
}
\`\`\`

## Access in onBar()

By the time \`onBar()\` runs, all indicator values are pre-computed:

\`\`\`javascript
function onBar(ctx, i) {
  // These are array lookups, not calculations
  const ema = ctx.ind.ema[i];
  const rsi = ctx.ind.rsi[i];
  const macdLine = ctx.ind.macd.macd[i];
  const macdSignal = ctx.ind.macd.signal[i];
}
\`\`\`

## Warm-up Period

Indicators need historical data before producing valid values:

| Indicator | Warm-up |
|-----------|---------|
| SMA(20) | 20 bars |
| EMA(20) | ~20 bars |
| RSI(14) | 15 bars |
| MACD(12,26,9) | 35 bars |

During warm-up, values are \`NaN\`:

\`\`\`javascript
function onBar(ctx, i) {
  // Always check for NaN
  if (q.isNaN(ctx.ind.ema[i])) return;
  
  // Now safe to use
}
\`\`\`

## Don't Compute Manually

\`\`\`javascript
// WRONG - Computing in onBar (slow)
function onBar(ctx, i) {
  let sum = 0;
  for (let j = i - 20; j <= i; j++) {
    sum += ctx.series.close[j];
  }
  const sma = sum / 20;
}

// RIGHT - Declare in init (fast)
function init(ctx) {
  ctx.indicator('sma', 'SMA', { period: 20 });
}

function onBar(ctx, i) {
  const sma = ctx.ind.sma[i]; // Pre-computed
}
\`\`\`

## Related

- [init()](/docs/api/lifecycle/init) — Declaring indicators
- [Available Indicators](/docs/api/lifecycle/init) — Full indicator list
`,
  },

  // Performance Metrics
  {
    id: 'engine/performance-metrics',
    title: 'Performance Metrics',
    category: 'engine',
    subcategory: 'execution',
    tags: ['metrics', 'performance', 'sharpe', 'drawdown', 'win rate', 'profit', 'statistics'],
    version: '2.0',
    related: ['engine/execution/order-timing', 'engine/fees', 'guides/debugging'],
    order: 6,
    description: 'How backtest performance metrics are calculated',
    content: `# Performance Metrics

After a backtest completes, the engine computes a set of statistics from the closed trades. These metrics appear in the results panel and are used as optimization targets.

## Core Metrics

| Metric | Formula | Description |
|--------|---------|-------------|
| **tradeCount** | Count of closed trades | Total trades executed |
| **wins** | Trades with profit > 0 | Number of winning trades |
| **losses** | Trades with profit <= 0 | Number of losing trades |
| **winRate** | wins / tradeCount | Win percentage (0-1) |
| **totalProfit** | Sum of all trade P&L | Net profit after fees |
| **totalFees** | Sum of all fees paid | Total commission cost |
| **totalReturn** | Final equity - initial | Absolute dollar return |
| **totalReturnPercent** | totalReturn / initial * 100 | Percentage return |
| **avgTradeLength** | Avg bars held per trade | Average holding period |

## Risk Metrics

### Max Drawdown

The largest peak-to-trough decline in equity during the backtest:

\`\`\`
maxDrawdown = max(peak - trough) for all equity peaks
maxDrawdownPercent = maxDrawdown / peak * 100
\`\`\`

A lower drawdown indicates better capital preservation. Strategies with 50%+ drawdowns are risky — a 50% loss requires a 100% gain to recover.

### Sharpe Ratio

Risk-adjusted return, measuring return per unit of volatility:

\`\`\`
sharpeRatio = mean(returns) / stdDev(returns) * sqrt(annualizationFactor)
\`\`\`

| Sharpe | Interpretation |
|--------|---------------|
| < 0 | Losing money |
| 0 - 1 | Below average |
| 1 - 2 | Good |
| 2 - 3 | Very good |
| > 3 | Excellent (verify, may be overfit) |

## Bidirectional Metrics

When using bidirectional trading mode, additional metrics break down performance by direction:

| Metric | Description |
|--------|-------------|
| **longTrades** | Number of long trades |
| **shortTrades** | Number of short trades |
| **longWinRate** | Win rate for long trades only |
| **shortWinRate** | Win rate for short trades only |
| **longProfit** | Total profit from long trades |
| **shortProfit** | Total profit from short trades |

## Optimization Targets

These metrics can be used as optimization objectives:

| Target | Direction | Best For |
|--------|-----------|----------|
| \`sharpeRatio\` | Maximize | Risk-adjusted performance |
| \`totalReturn\` | Maximize | Raw profitability |
| \`profitFactor\` | Maximize | Gross profit / gross loss |
| \`maxDrawdown\` | Minimize | Capital preservation |
| \`winRate\` | Maximize | Consistency |

## Interpreting Results

**High win rate, low profit:** Many small wins and a few large losses. Add stop losses.

**Low win rate, high profit:** Few big winners carry the strategy. Common with trend-following — accept many small losses for occasional large moves.

**High Sharpe, low trade count:** May be overfit to a few lucky trades. Verify with more data or walk-forward analysis.

**Negative totalReturn with positive winRate:** Losing trades are larger than winners. Adjust risk-reward ratio.

## Related

- [Commissions & Fees](/docs/engine/fees) — How fees impact metrics
- [Debugging](/docs/guides/debugging) — Diagnosing poor metrics
`,
  },

  // Commissions & Fees
  {
    id: 'engine/fees',
    title: 'Commissions & Fees',
    category: 'engine',
    subcategory: 'execution',
    tags: ['fees', 'commissions', 'slippage', 'cost', 'trading costs'],
    version: '2.0',
    related: ['engine/execution/order-timing', 'engine/fills/marketOrders', 'engine/performance-metrics', 'engine/fills/slippageModel'],
    order: 7,
    description: 'How trading fees and slippage are modeled in backtests',
    content: `# Commissions & Fees

The backtest engine simulates realistic trading costs to prevent overly optimistic results. Both commissions and slippage are applied automatically.

## Commission Model

A percentage-based fee is applied on **both entry and exit** of every trade:

\`\`\`
fee = (tradeAmount * feePercent) / 100
\`\`\`

| Parameter | Default | Range | Description |
|-----------|---------|-------|-------------|
| \`feePercent\` | 0.1% | 0 - 5% | Fee percentage per side |

### Both Sides

Fees are charged twice per round-trip trade:
- **Entry fee:** Applied when a position is opened
- **Exit fee:** Applied when a position is closed

\`\`\`
Example: $10,000 trade at 0.1% fee
  Entry fee:  $10,000 * 0.001 = $10.00
  Exit fee:   $10,200 * 0.001 = $10.20
  Total fees: $20.20 (0.2% round trip)
\`\`\`

### Impact on Strategy Design

- A 0.1% fee (0.2% round trip) means a trade must gain more than 0.2% to be profitable
- High-frequency strategies (many trades) accumulate significant fee drag
- Monitor \`totalFees\` in results to understand cost impact

\`\`\`
Example: 200 trades at $10,000 each, 0.1% fee
  Total fees: 200 * 2 * $10 = $4,000
  Strategy must generate > $4,000 profit to break even
\`\`\`

## Slippage Model

Slippage simulates the price impact of order execution — real markets don't always fill at the exact price you expect.

\`\`\`
fillPrice = basePrice * (1 ± slippagePercent / 100)
\`\`\`

Slippage is always applied in the **adverse direction**:
- **Buying:** Price moves up (you pay more)
- **Selling:** Price moves down (you receive less)

| Parameter | Default | Range | Description |
|-----------|---------|-------|-------------|
| \`slippagePercent\` | 0% | 0 - 10% | Price impact per fill |

### Slippage + Fees Together

Both are applied on every fill:

\`\`\`
Example: Buy at $50,000 open, 0.05% slippage, 0.1% fee
  Fill price: $50,000 * 1.0005 = $50,025    (slippage)
  Fee:        $50,025 * 0.001  = $50.03      (commission)
  Total cost: $50,025.00 entry + $50.03 fee
\`\`\`

## Configuring Costs

Set fee and slippage values in the backtest configuration panel. Both parameters appear in the cost settings:

- **Fee %:** Commission per side (default 0.1%)
- **Slippage %:** Price impact per fill (default 0%)

For realistic crypto exchange simulation:
- **Maker/taker fees:** 0.1% is typical for major exchanges
- **Slippage:** 0.01% - 0.05% for liquid pairs, higher for illiquid ones

## Comparing Gross vs Net

Always check \`totalProfit\` (net of fees) against the number of trades:

\`\`\`
Gross profit: $5,000
Total fees:   $2,000
Net profit:   $3,000
Fee ratio:    40% of gross profit consumed by fees
\`\`\`

If fees consume more than 30% of gross profit, consider:
- Fewer trades (wider entry/exit criteria)
- Longer holding periods
- Removing signals in choppy/sideways markets

## Related

- [Performance Metrics](/docs/engine/performance-metrics) — How metrics account for fees
- [Slippage Model](/docs/engine/fills/slippageModel) — Detailed slippage implementation
- [Order Timing](/docs/engine/execution/order-timing) — When fills occur
`,
  },
];
